import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./styles/SwipeBlock.css";
import slide1Image from "../assets/slide-1.svg";
import slide2Image from "../assets/slide-2.svg";
import slide3Image from "../assets/slide-3.svg";
import HollandPairTest from "./HollandPairTest";
import HollandEducationResults from "./HollandEducationResults";
import HollandCompaniesResults from "./HollandCompaniesResults";
import { loadHollandResults, clearHollandResults } from "../../helpers/hollandStorage";

const SLIDES = [
  {
    id: "proftest",
    title: "Профтестирование",
    subtitle: "Выявляем сильные стороны и зоны роста абитуриента",
    details:
      "Система профтестирования помогает подобрать направление обучения и будущую профессию на основе интересов, склонностей и текущих навыков.",
    image: slide1Image,
  },
  {
    id: "education",
    title: "Учебные заведения республики",
    subtitle: "Подбор колледжей, техникумов и вузов по профилю",
    details:
      "Показываем учебные заведения республики с фильтрами по направлениям, формату обучения и проходным требованиям, чтобы пользователь видел релевантные варианты.",
    image: slide2Image,
  },
  {
    id: "companies",
    title: "Предприятия и организации республики",
    subtitle: "Показываем карьерные маршруты и работодателей региона",
    details:
      "После выбора направления пользователь видит компании и организации республики, где востребованы соответствующие компетенции, а также возможные карьерные треки.",
    image: slide3Image,
  },
];

export default function SwipeBlock() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [hollandData, setHollandData] = useState(() => loadHollandResults());
  const [retakeKey, setRetakeKey] = useState(0);
  const [showRetakeTest, setShowRetakeTest] = useState(false);
  const activeSlide = useMemo(
    () => (activeIndex === null ? null : SLIDES[activeIndex]),
    [activeIndex]
  );
  const [touchStartX, setTouchStartX] = useState(null);

  const goPrev = () => {
    setActiveIndex((prev) => {
      if (prev === null) return SLIDES.length - 1;
      return prev === 0 ? SLIDES.length - 1 : prev - 1;
    });
  };

  const goNext = () => {
    setActiveIndex((prev) => {
      if (prev === null) return 0;
      return prev === SLIDES.length - 1 ? 0 : prev + 1;
    });
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.changedTouches[0].clientX);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) return;

    const deltaX = event.changedTouches[0].clientX - touchStartX;
    const minSwipeDistance = 40;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        goPrev();
      } else {
        goNext();
      }
    }

    setTouchStartX(null);
  };

  const handleTestComplete = (payload) => {
    setHollandData(payload);
    setShowRetakeTest(false);
  };

  const renderSlideContent = () => {
    if (!activeSlide) return null;

    if (activeSlide.id === "proftest") {
      return (
        <div className="slide-content slide-content--proftest">
          {hollandData && !showRetakeTest ? (
            <div className="slide-content-saved">
              <p>
                Результаты сохранены. Код Холланда:{" "}
                <strong>{hollandData.hollandCode}</strong>
              </p>
              <div className="slide-content-actions">
                <button
                  type="button"
                  className="slide-content-btn"
                  onClick={() => {
                    clearHollandResults();
                    setHollandData(null);
                    setRetakeKey((value) => value + 1);
                    setShowRetakeTest(true);
                  }}
                >
                  Пройти заново
                </button>
                <Link to="/fschool/holland-results" className="slide-content-link">
                  Полная сводка
                </Link>
              </div>
            </div>
          ) : (
            <HollandPairTest
              key={retakeKey}
              compact
              onComplete={handleTestComplete}
            />
          )}
        </div>
      );
    }

    if (activeSlide.id === "education") {
      return (
        <div className="slide-content slide-content--education">
          <HollandEducationResults
            key={hollandData?.completedAt || "empty"}
            hollandData={hollandData}
          />
        </div>
      );
    }

    if (activeSlide.id === "companies") {
      return (
        <div className="slide-content slide-content--companies">
          <HollandCompaniesResults
            key={hollandData?.completedAt || "empty"}
            hollandData={hollandData}
          />
        </div>
      );
    }

    return (
      <div className="slide-content">
        <span className="slide-content-kicker">Направление</span>
        <h4>{activeSlide.title}</h4>
        <p className="slide-content-subtitle">{activeSlide.subtitle}</p>
        <p>{activeSlide.details}</p>
      </div>
    );
  };

  return (
    <section className="swipe-block">
      <h2 className="swipe-block-title">Навигатор профессионального развития</h2>

      <div className="swipe-track-wrap">
        <button className="nav-btn" type="button" onClick={goPrev} aria-label="Назад">
          ←
        </button>

        <div
          className="swipe-track"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {SLIDES.map((slide, index) => (
            <article
              key={slide.id}
              className={`slide-card slide-card--pos-${index} ${index === activeIndex ? "active" : ""}`}
            >
              <img className="slide-image" src={slide.image} alt={slide.title} />
              <button
                type="button"
                className="slide-overlay"
                onClick={() => setActiveIndex(index)}
                aria-label={`Открыть: ${slide.title}`}
              >
                <h3>{slide.title}</h3>
              </button>
              <span className="slide-marker" aria-hidden="true" />
            </article>
          ))}
        </div>

        <button className="nav-btn" type="button" onClick={goNext} aria-label="Вперед">
          →
        </button>
      </div>

      {renderSlideContent()}
    </section>
  );
}
